(() => {
  if (customElements.get('x-modal')) return;

  class XModal extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      this.shadowRoot.innerHTML = `
        <style>
          :host { display: contents; }

          :host{
            --x-modal-max-w: min(92vw, 920px);
            --x-modal-max-h: 86vh;
            --x-modal-radius: 8px;
            --x-modal-bg: #fff;
            --x-modal-padding: 0px;
            --x-modal-shadow: 0 12px 36px rgba(0,0,0,.18);
            --x-modal-backdrop: rgba(0,0,0,.2);

            --x-modal-mobile-max-h: min(92vh, 640px);
            --x-modal-mobile-radius: 16px 16px 0 0;
          }

          .backdrop{
            position: fixed; inset: 0;
            display: flex; align-items: center; justify-content: center;
            background: var(--x-modal-backdrop);
            z-index: 2147483647;
            opacity: 0; visibility: hidden; pointer-events: none;
            transition: opacity .18s ease-out, visibility 0s linear .18s;
          }
          :host([open]) .backdrop{
            opacity: 1; visibility: visible; pointer-events: auto;
            transition: opacity .18s ease-out, visibility 0s;
          }

          .panel-wrap{ position: relative; }

          :host-context(html[data-scheme="dark"]) {
            --x-modal-bg: #000;
            --x-modal-backdrop: rgba(0,0,0,.6);
            color: #fff; /* kolor tekstu hosta, dziedziczy w dół */
          }

          .panel{
            background: var(--x-modal-bg);
            width: var(--x-modal-max-w);
            max-height: var(--x-modal-max-h);
            border-radius: var(--x-modal-radius);
            box-shadow: var(--x-modal-shadow);
            padding: var(--x-modal-padding);
            transform: translateY(8px) scale(.98);
            opacity: 0;
            transition: transform .18s cubic-bezier(.2,.7,.3,1), opacity .18s ease-out;
            will-change: transform, opacity;
            overflow: auto;
            display: flex;
            flex-direction: column;
            padding: 2.4rem 1.6rem 2.2rem 1.6rem;
            box-sizing: border-box;
          }

          :host([open]) .panel{ transform:none; opacity:1; }

          .panel-content {
            overflow: auto;
          }

          .close-button-bottom {
            margin-top: 2.5rem;
            min-height: 5.5rem;
            width: 100%;
            border-radius: .8rem;
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 1.4rem;
            color: #505050;
            font-weight: 500;
            background: #E9E9E9;
            cursor: pointer;
            border: none;
            column-gap: 1.6rem;
          }

          @media (pointer: fine) {
            .close-button-bottom:hover {
              background: #DEDEDE;
            }
          }

          @media (prefers-reduced-motion: reduce){
            .backdrop, .panel { transition: none !important; }
          }


          @media (max-width: 749px){
            .backdrop{
              display: flex;
              align-items: flex-end;      
              justify-content: center;
            }
            .panel-wrap{
              width: 100%;
            }
            .panel{
              width: 100vw;               
              max-width: 100vw;
              max-height: var(--x-modal-mobile-max-h); 
              border-radius: var(--x-modal-mobile-radius);
              box-shadow: 0 -8px 24px rgba(0,0,0,.18);
              transform: translateY(100%) scale(1);
              opacity: 1;                  
              transition: transform 0.3s cubic-bezier(0,0,0.2,1),
                opacity 0.3s cubic-bezier(0,0,0.2,1);
              will-change: transform;
              overscroll-behavior: contain;
            }
            :host([open]) .panel{
              transform: translateY(0);
            }

            .close .close-text {
              display: block;
              font-size: 1.4rem;
              line-height: 160%;
              font-weight: 600;
              color: #484848;
            }
              
            .panel{
              padding-bottom: max(var(22px), env(safe-area-inset-bottom));
            }
          }
        </style>

        <div class="backdrop" data-modal-close>
          <div class="panel-wrap">
            <div class="panel" role="dialog" aria-modal="true" aria-labelledby="modal-title">
              <slot></slot>
              <button class="close-button-bottom" type="button" aria-label="Close" data-modal-close>
                <svg class="arrow-down-icon" width="13" height="7" viewBox="0 0 13 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.5 0.499999L6.5 6.5L0.5 0.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span class="close-text"></span>
              </button>
            </div>
          </div>
        </div>
      `;

      this._onDocClick = this._onDocClick.bind(this);
      this._onKey = this._onKey.bind(this);
      this._onShadowClick = this._onShadowClick.bind(this);
    }

    connectedCallback(){
      if (this.id) {
        const dup = [...document.querySelectorAll(`x-modal#${CSS.escape(this.id)}`)].find(el => el !== this);
        if (dup) dup.remove();
      }
      if (this.parentNode !== document.body) document.body.appendChild(this);
      document.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onKey);
      this.shadowRoot.addEventListener('click', this._onShadowClick);
      this.shadowRoot.querySelector('.close-text').textContent = window.accessibilityStrings?.close || 'Zamknij';
    }
    disconnectedCallback(){
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('keydown', this._onKey);
      this.shadowRoot.removeEventListener('click', this._onShadowClick);
    }

    _onDocClick(e){
      const openBtn = e.target.closest?.('[data-modal-open]');
      if (openBtn && openBtn.getAttribute('data-modal-open') === this.id) {
        e.preventDefault();
        this.open();
      }
    }
    _onShadowClick(e){
      if (e.target.closest('[data-modal-close]')) { e.preventDefault(); this.close(); return; }
      const bd = this.shadowRoot.querySelector('.backdrop');
      if (e.target === bd) this.close();
    }
    _onKey(e){
      if (!this.hasAttribute('open')) return;
      if (e.key === 'Escape') { e.preventDefault(); this.close(); }
    }

    open(){
      this.setAttribute('open','');
      const docEl = document.documentElement, body = document.body;
      const n = +(body.dataset.gmLockCount || 0) + 1;
      body.dataset.gmLockCount = String(n);
      if (n === 1) {
        const sbw = window.innerWidth - docEl.clientWidth;
        body.dataset.gmPrevOverflow = body.style.overflow || '';
        body.dataset.gmPrevPaddingRight = getComputedStyle(body).paddingRight;
        docEl.style.setProperty('--width-scrollbar', sbw + 'px');
        if (sbw > 0) {
          const pr = parseFloat(getComputedStyle(body).paddingRight) || 0;
          body.style.paddingRight = (pr + sbw) + 'px';
        }
        body.style.overflow = 'hidden';
      }
    }
    close(){
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      const body = document.body, docEl = document.documentElement;
      const n = Math.max((+(body.dataset.gmLockCount || 1)) - 1, 0);
      body.dataset.gmLockCount = String(n);
      if (n === 0) {
        body.style.overflow = body.dataset.gmPrevOverflow || '';
        body.style.paddingRight = body.dataset.gmPrevPaddingRight || '';
        delete body.dataset.gmPrevOverflow;
        delete body.dataset.gmPrevPaddingRight;
        docEl.style.removeProperty('--width-scrollbar');
      }
    }
  }

  customElements.define('x-modal', XModal);
})();
